import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';

interface AddPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
  showLaterButton?: boolean;
}

export default function AddPaymentMethodModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  title = "Add Payment Method",
  description = "Add a credit or debit card to make purchases",
  showLaterButton = true
}: AddPaymentMethodModalProps) {
  const { theme } = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Stripe renders the card field in an iframe, so none of the page's CSS
  // reaches it - every colour has to be passed explicitly and kept in step with
  // the theme by hand.
  //
  // Deliberately no backgroundColor: setting one painted the iframe a different
  // dark from the container around it, which showed as a darker box inside a
  // lighter one. Leaving it transparent lets the container provide the surface,
  // so there is only ever one.
  const isDark = theme === 'dark';
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        // Near-white on the dark surface: the previous value read as grey
        // against the container it actually sits on.
        color: isDark ? '#f8fafc' : '#111827',
        fontFamily: 'inherit',
        fontSmoothing: 'antialiased',
        iconColor: isDark ? '#94a3b8' : '#6b7280',
        '::placeholder': {
          color: isDark ? '#94a3b8' : '#9ca3af',
        },
        // Chrome paints autofilled fields with its own yellow highlight and
        // dark text. Inside the Stripe iframe the usual page-level workaround
        // is unreachable, but Stripe exposes this pseudo-class in its style
        // API, which is the only way to stop a filled card reading as a yellow
        // bar in the middle of a dark modal.
        ':-webkit-autofill': {
          color: isDark ? '#f8fafc' : '#111827',
          // Matches dark-tertiary, the container's background, so an autofilled
          // field is indistinguishable from a typed one.
          backgroundColor: isDark ? '#334155' : '#ffffff',
        },
        ':focus': {
          color: isDark ? '#f8fafc' : '#111827',
        },
      },
      complete: {
        color: isDark ? '#f8fafc' : '#111827',
        iconColor: isDark ? '#4ade80' : '#16a34a',
      },
      invalid: {
        color: isDark ? '#fca5a5' : '#dc2626',
        iconColor: isDark ? '#fca5a5' : '#dc2626',
      },
    },
  };

  const handleAddCard = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Validate card element before proceeding
      const { error: cardError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (cardError) {
        let errorMessage = cardError.message || 'Invalid card information';
        
        // Provide more specific error messages
        if (cardError.code === 'expired_card') {
          errorMessage = 'This card has expired. Please use a different card.';
        } else if (cardError.code === 'incorrect_cvc') {
          errorMessage = 'The security code (CVC) is incorrect. Please check and try again.';
        } else if (cardError.code === 'incorrect_number') {
          errorMessage = 'The card number is incorrect. Please check and try again.';
        } else if (cardError.code === 'invalid_expiry_month' || cardError.code === 'invalid_expiry_year') {
          errorMessage = 'The expiration date is invalid. Please check and try again.';
        } else if (cardError.code === 'incomplete_number') {
          errorMessage = 'Please enter a complete card number.';
        } else if (cardError.code === 'incomplete_cvc') {
          errorMessage = 'Please enter a complete security code (CVC).';
        } else if (cardError.code === 'incomplete_expiry') {
          errorMessage = 'Please enter a complete expiration date.';
        }
        
        throw new Error(errorMessage);
      }

      // Create setup intent
      const setupIntentResponse = await apiService.createSetupIntent();
      if (!setupIntentResponse.success) {
        throw new Error(setupIntentResponse.error.message);
      }

      // Confirm setup intent with card
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        setupIntentResponse.data.clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
          return_url: `${window.location.origin}/payment-methods?setup=success`
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (setupIntent?.payment_method) {
        // Attach payment method to customer
        const attachResponse = await apiService.attachPaymentMethod(setupIntent.payment_method as string);
        if (!attachResponse.success) {
          // Provide more specific error messages based on error code
          let errorMessage = attachResponse.error.message;
          
          if (attachResponse.error.code === 'INVALID_PAYMENT_METHOD') {
            if (errorMessage.includes('expired')) {
              errorMessage = 'This card has expired. Please use a different card.';
            } else if (errorMessage.includes('already attached to this customer') || 
                       errorMessage.includes('already attached to a different customer')) {
              errorMessage = 'This card is already saved to an account. Please use a different card.';
            } else if (errorMessage.includes('Invalid payment method')) {
              errorMessage = 'Invalid card information. Please check your card details and try again.';
            }
          }
          
          throw new Error(errorMessage);
        }

        setSuccess('Payment method added successfully!');
        
        // Call success callback after a short delay
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1500);
      }
    } catch (err) {
      let errorMessage = 'Failed to add payment method';
      
      if (err instanceof Error) {
        errorMessage = err.message;
        
        // Handle specific Stripe errors
        if (err.message.includes('Your card was declined')) {
          errorMessage = 'Your card was declined. Please check your card details or try a different card.';
        } else if (err.message.includes('expired')) {
          errorMessage = 'This card has expired. Please use a different card.';
        } else if (err.message.includes('insufficient_funds')) {
          errorMessage = 'Your card has insufficient funds. Please try a different card.';
        } else if (err.message.includes('incorrect_cvc')) {
          errorMessage = 'The security code (CVC) is incorrect. Please check and try again.';
        } else if (err.message.includes('incorrect_number')) {
          errorMessage = 'The card number is incorrect. Please check and try again.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  // Show message if Stripe is not configured
  if (!stripe) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-dark-secondary rounded-lg p-6 w-full max-w-md">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <div className="text-4xl mb-2">⚠️</div>
            <p className="font-medium">Payment system not configured</p>
            <p className="text-sm">Stripe configuration is required to add payment methods</p>
            <button
              onClick={handleClose}
              className="btn btn-secondary mt-4"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-secondary rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {description}
          </p>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-green-700 dark:text-green-300">
                {success}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleAddCard} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Card Information
            </label>
            {/* The surface the card field sits on. It has to be a real colour
                rather than transparent, because the element above deliberately
                paints none of its own. */}
            <div className="px-3 py-3.5 border rounded-md transition-colors bg-white border-gray-300 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/30 dark:bg-dark-tertiary dark:border-dark-secondary dark:focus-within:border-primary-400">
              <CardElement options={cardElementOptions} />
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              type="submit"
              disabled={!stripe || loading}
              className="flex-1 btn btn-primary"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </div>
              ) : (
                'Add Payment Method'
              )}
            </button>
            {showLaterButton ? (
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="btn btn-secondary"
              >
                Later
              </button>
            ) : (
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          <p>🔒 Your payment information is secure and encrypted</p>
          <p>Powered by Stripe</p>
        </div>
      </div>
    </div>
  );
}
